import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE = '/api';

// same theme system as volunteer page
const THEME_STYLES: Record<string, { bg: React.CSSProperties; overlay?: string; tw?: string }> = {
  blue:   { tw: 'from-[#0D47A1] via-[#1565C0] to-[#1976D2]', bg: {} },
  purple: { tw: 'from-[#4527A0] via-[#6200EA] to-[#7B1FA2]', bg: {} },
  sunset: { tw: 'from-[#E65100] via-[#F57C00] to-[#EF6C00]', bg: {} },
  green:  { tw: 'from-[#1B5E20] via-[#2E7D32] to-[#388E3C]', bg: {} },
  dark:   { tw: 'from-[#1A1A2E] via-[#16213E] to-[#0F3460]', bg: {} },
  ocean:  { tw: 'from-[#006064] via-[#00838F] to-[#0097A7]', bg: {} },
  aurora: { bg: { background: 'linear-gradient(135deg,#1A237E 0%,#4A148C 33%,#006064 66%,#1B5E20 100%)' } },
  fire:   { bg: { background: 'linear-gradient(135deg,#BF360C 0%,#E64A19 25%,#FF6F00 60%,#F9A825 100%)' } },
  galaxy: { bg: { background: 'linear-gradient(135deg,#0A0A0A 0%,#1A0030 40%,#0D47A1 70%,#6200EA 100%)' } },
  photo_hands: { bg: { backgroundImage: 'url(https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=1200&q=80)', backgroundSize:'cover', backgroundPosition:'center' }, overlay: 'rgba(13,71,161,0.7)' },
  photo_team:  { bg: { backgroundImage: 'url(https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=1200&q=80)', backgroundSize:'cover', backgroundPosition:'center' }, overlay: 'rgba(98,0,234,0.65)' },
  photo_nature:{ bg: { backgroundImage: 'url(https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1200&q=80)', backgroundSize:'cover', backgroundPosition:'center' }, overlay: 'rgba(27,94,32,0.65)' },
};

interface FormData {
  _id: string;
  title: string;
  subtitle?: string;
  bulletPoints?: string[];
  ctaText?: string;
  theme?: string;
  heroImageUrl?: string;
  heroVideoUrl?: string;
  fields: { id: string; label: string; type: string; required: boolean; options?: string[]; placeholder?: string }[];
  orgId: { name: string; logo?: string };
  projectId?: { name: string };
  formType: string;
}

const PublicFormPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [formData, setFormData] = useState<FormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [values, setValues] = useState<Record<string, any>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchForm = async () => {
      try {
        const res = await axios.get(`${API_BASE}/public-forms/${slug}`);
        setFormData(res.data);
        const init: Record<string, any> = {};
        res.data.fields.forEach((f: any) => { init[f.id] = f.type === 'checkbox' ? false : ''; });
        setValues(init);
      } catch { setNotFound(true); }
      finally { setLoading(false); }
    };
    if (slug) fetchForm();
  }, [slug]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API_BASE}/public-forms/${slug}/submit`, values);
      setSubmitted(true);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'שגיאה בשמירת הרשמה');
    } finally { setSubmitting(false); }
  };

  const themeKey = formData?.theme || 'blue';
  const themeStyle = THEME_STYLES[themeKey] || THEME_STYLES.blue;
  const hasTW = !!themeStyle.tw;
  const bgStyle: React.CSSProperties = hasTW ? {} : themeStyle.bg;
  const overlay = themeStyle.overlay || '';
  const wClass = hasTW ? `min-h-screen bg-gradient-to-br ${themeStyle.tw}` : 'min-h-screen';

  if (loading) return (
    <div className={hasTW ? `min-h-screen flex items-center justify-center bg-gradient-to-br ${themeStyle.tw}` : 'min-h-screen flex items-center justify-center'} style={bgStyle}>
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent" />
    </div>
  );

  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0D47A1] to-[#1976D2]" dir="rtl">
      <div className="text-center text-white"><p className="text-6xl mb-4">😕</p><h1 className="text-2xl font-bold">הטופס לא נמצא</h1></div>
    </div>
  );

  if (submitted) return (
    <div className={wClass} style={bgStyle} dir="rtl">
      {overlay && <div className="fixed inset-0 z-0" style={{ background: overlay }} />}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-6 md:p-10 max-w-md w-full text-center">
          <div className="text-5xl mb-4">✅</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">תודה רבה!</h2>
          <p className="text-gray-500">ההרשמה שלך התקבלה בהצלחה.</p>
          <p className="text-gray-400 text-sm mt-2">{formData?.orgId?.name} תיצור איתך קשר בקרוב 🙌</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className={wClass} style={bgStyle} dir="rtl">
      {overlay && <div className="fixed inset-0 z-0 pointer-events-none" style={{ background: overlay }} />}

      {/* Hero */}
      {(formData?.heroVideoUrl || formData?.heroImageUrl) && (
        <div className="relative z-10">
          {formData.heroVideoUrl
            ? <video src={formData.heroVideoUrl} className="w-full h-64 object-cover" muted loop autoPlay playsInline />
            : <img src={formData.heroImageUrl} alt="hero" className="w-full h-64 object-cover" />}
          <div className="absolute inset-0 bg-black/30" />
        </div>
      )}

      <div className="relative z-10 py-10 px-4">
        <div className="max-w-lg mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            {formData?.orgId?.logo && (
              <img src={formData.orgId.logo} alt="לוגו" className="h-14 w-auto object-contain mx-auto mb-4 drop-shadow-lg" />
            )}
            <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-3 drop-shadow-sm">{formData?.title}</h1>
            {formData?.subtitle && <p className="text-white/80 text-lg">{formData.subtitle}</p>}
            {formData?.bulletPoints?.filter(Boolean).map((b, i) => (
              <div key={i} className="flex items-center gap-2 text-white/80 text-sm mt-1 justify-center">
                <span className="text-green-300">✓</span> {b}
              </div>
            ))}
            {formData?.projectId && <p className="text-white/50 text-xs mt-2">📁 {formData.projectId.name}</p>}
            <p className="text-white/40 text-xs mt-1">{formData?.orgId?.name}</p>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className={hasTW ? `h-1.5 bg-gradient-to-l ${themeStyle.tw}` : 'h-1.5 bg-[#1565C0]'} />
            <form onSubmit={handleSubmit} className="p-4 md:p-7 space-y-4">
              {formData?.fields.map(field => (
                <div key={field.id}>
                  <label className="block text-sm font-semibold text-gray-600 mb-1.5">
                    {field.label}{field.required && <span className="text-red-400 mr-1">*</span>}
                  </label>
                  {field.type === 'select' ? (
                    <select value={values[field.id] || ''} onChange={e => setValues(v => ({...v, [field.id]: e.target.value}))}
                      required={field.required}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#1565C0] outline-none bg-white">
                      <option value="">בחר...</option>
                      {field.options?.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : field.type === 'radio' ? (
                    <div className="space-y-2">
                      {field.options?.map(o => (
                        <label key={o} className="flex items-center gap-3 cursor-pointer p-3 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors">
                          <input type="radio" name={field.id} value={o} checked={values[field.id] === o}
                            onChange={() => setValues(v => ({...v, [field.id]: o}))}
                            className="accent-[#1565C0]" required={field.required && !values[field.id]} />
                          <span className="text-sm text-gray-700">{o}</span>
                        </label>
                      ))}
                    </div>
                  ) : field.type === 'multiselect' ? (
                    <div className="space-y-2">
                      {field.options?.map(o => {
                        const selected = (values[field.id] || []) as string[];
                        return (
                          <label key={o} className={`flex items-center gap-3 cursor-pointer p-3 rounded-xl border transition-colors ${selected.includes(o) ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}>
                            <input type="checkbox" checked={selected.includes(o)}
                              onChange={e => setValues(v => ({...v, [field.id]: e.target.checked ? [...selected, o] : selected.filter((x: string) => x !== o)}))}
                              className="accent-[#1565C0]" />
                            <span className="text-sm text-gray-700">{o}</span>
                          </label>
                        );
                      })}
                    </div>
                  ) : field.type === 'rating' ? (
                    <div className="flex gap-2">
                      {[1,2,3,4,5].map(n => (
                        <button key={n} type="button" onClick={() => setValues(v => ({...v, [field.id]: n}))}
                          className={`text-3xl transition-transform hover:scale-110 ${(values[field.id] || 0) >= n ? '' : 'grayscale opacity-40'}`}>
                          ⭐
                        </button>
                      ))}
                      {values[field.id] && <span className="text-sm text-gray-500 self-center mr-2">{values[field.id]}/5</span>}
                    </div>
                  ) : field.type === 'scale' ? (
                    <div className="flex flex-wrap gap-2">
                      {[1,2,3,4,5,6,7,8,9,10].map(n => (
                        <button key={n} type="button" onClick={() => setValues(v => ({...v, [field.id]: n}))}
                          className={`w-10 h-10 rounded-xl border-2 text-sm font-bold transition-all ${values[field.id] === n ? 'border-blue-500 bg-blue-500 text-white' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'}`}>
                          {n}
                        </button>
                      ))}
                    </div>
                  ) : field.type === 'checkbox' ? (
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input type="checkbox" checked={!!values[field.id]} onChange={e => setValues(v => ({...v, [field.id]: e.target.checked}))}
                        className="w-5 h-5 rounded accent-[#1565C0]" />
                      <span className="text-sm text-gray-600">{field.placeholder || 'כן'}</span>
                    </label>
                  ) : field.type === 'textarea' ? (
                    <textarea value={values[field.id] || ''} onChange={e => setValues(v => ({...v, [field.id]: e.target.value}))}
                      required={field.required} rows={3} placeholder={field.placeholder}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#1565C0] outline-none resize-none" />
                  ) : (
                    <input
                      type={field.type === 'phone' ? 'tel' : field.type === 'email' ? 'email' : field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : 'text'}
                      value={values[field.id] || ''} onChange={e => setValues(v => ({...v, [field.id]: e.target.value}))}
                      required={field.required} placeholder={field.placeholder}
                      dir={field.type === 'email' || field.type === 'phone' ? 'ltr' : 'rtl'}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#1565C0] outline-none" />
                  )}
                </div>
              ))}

              {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">⚠️ {error}</div>}

              <button type="submit" disabled={submitting}
                className={`w-full py-4 text-white font-bold rounded-2xl transition-all shadow-lg disabled:opacity-50 ${hasTW ? `bg-gradient-to-l ${themeStyle.tw}` : ''}`}
                style={hasTW ? {} : { background: '#1565C0' }}>
                {submitting ? <div className="flex items-center justify-center gap-2"><div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />שולח...</div> : (formData?.ctaText || 'שלח הרשמה ✅')}
              </button>
            </form>
          </div>

          <p className="text-center text-white/30 text-xs mt-6">מופעל על ידי Amutech · מערכת ניהול עמותות</p>
        </div>
      </div>
    </div>
  );
};

export default PublicFormPage;
